import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Video, 
  Link as LinkIcon, 
  Download, 
  ExternalLink,
  Search,
  Filter,
  BookOpen,
  FileSpreadsheet,
  Play,
  Globe,
  ArrowRight,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const resourceTypeIcons = {
  document: FileText,
  article: BookOpen,
  video: Video,
  external_link: Globe,
};

const resourceTypeColors = {
  document: 'bg-blue-100 text-blue-600 border-blue-200',
  article: 'bg-purple-100 text-purple-600 border-purple-200',
  video: 'bg-red-100 text-red-600 border-red-200',
  external_link: 'bg-green-100 text-green-600 border-green-200',
};

const categories = [
  'All',
  'Reports',
  'Whitepapers',
  'Policy Briefs',
  'Research',
  'Webinars',
  'Partner Resources',
];

const resourceTypes = [
  { id: 'all', label: 'All Types' },
  { id: 'document', label: 'Documents' },
  { id: 'article', label: 'Articles' },
  { id: 'video', label: 'Videos' },
  { id: 'external_link', label: 'External Links' },
];

// Featured Resources section with static data for demo
const featuredResources = [
  {
    id: 'featured-1',
    title: 'The HSL Impact Model: A Comprehensive Guide',
    description: 'Deep dive into our proprietary methodology for health systems transformation.',
    resource_type: 'document',
    category: 'Whitepapers',
    file_url: '#',
    author: 'HSL Research Team',
    is_featured: true,
  },
  {
    id: 'featured-2',
    title: 'Advanced Analytics in Practice: Case Studies from Africa',
    description: 'Real-world applications of rigorous analytical methods in health policy.',
    resource_type: 'article',
    category: 'Research',
    external_url: '#',
    author: 'Dr. Emmanuel Okonkwo',
    is_featured: true,
  },
  {
    id: 'featured-3',
    title: 'Health Systems Transformation Webinar Series',
    description: 'Watch our expert panel discuss strategies for building resilient health systems.',
    resource_type: 'video',
    category: 'Webinars',
    external_url: '#',
    author: 'HSL Leadership',
    is_featured: true,
  },
];

const partnerLinks = [
  {
    name: 'World Health Organization',
    description: 'Global health guidelines and resources',
    url: 'https://www.who.int',
    logo: 'WHO',
  },
  {
    name: 'The Global Fund',
    description: 'Resources on HIV, TB and Malaria programs',
    url: 'https://www.theglobalfund.org',
    logo: 'GF',
  },
  {
    name: 'Africa CDC',
    description: 'Continental health security resources',
    url: 'https://africacdc.org',
    logo: 'ACDC',
  },
  {
    name: 'USAID Global Health',
    description: 'Development and health resources',
    url: 'https://www.usaid.gov/global-health',
    logo: 'USAID',
  },
];

const ResourceCard = ({ resource, onDownload }) => {
  const Icon = resourceTypeIcons[resource.resource_type] || FileText;
  const colorClass = resourceTypeColors[resource.resource_type] || resourceTypeColors.document;

  const handleClick = () => {
    if (resource.file_url && resource.file_url !== '#') {
      onDownload(resource.id);
      window.open(resource.file_url, '_blank');
    } else if (resource.external_url && resource.external_url !== '#') {
      window.open(resource.external_url, '_blank');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white border border-slate-200 p-6 hover:border-teal-200 transition-colors group cursor-pointer"
      onClick={handleClick}
      data-testid={`resource-card-${resource.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 flex items-center justify-center border ${colorClass}`}>
          <Icon size={24} />
        </div>
        <div className="flex-1">
          <span className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 block">
            {resource.category}
          </span>
          <h3 className="font-heading text-lg text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
            {resource.title}
          </h3>
          <p className="text-slate-500 text-sm leading-relaxed mb-3">
            {resource.description}
          </p>
          {resource.author && (
            <p className="text-slate-400 text-xs">By {resource.author}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          {resource.resource_type === 'document' ? (
            <Download size={20} className="text-slate-400 group-hover:text-teal-600 transition-colors" />
          ) : (
            <ExternalLink size={20} className="text-slate-400 group-hover:text-teal-600 transition-colors" />
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch(`${API}/resources`);
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const trackDownload = async (resourceId) => {
    try {
      await fetch(`${API}/resources/${resourceId}/download`, { method: 'POST' });
    } catch (error) {
      console.error('Failed to track download:', error);
    }
  };

  // Combine API resources with featured static resources
  const allResources = [...featuredResources, ...resources];

  const filteredResources = allResources.filter((r) => {
    const matchesSearch = r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || r.category === selectedCategory;
    const matchesType = selectedType === 'all' || r.resource_type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  return (
    <div data-testid="resources-page">
      {/* Hero Section */}
      <section className="section-padding pt-32 bg-slate-50" data-testid="resources-hero">
        <div className="container-main">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <motion.span 
              variants={itemVariants}
              className="font-mono text-xs uppercase tracking-widest text-teal-600 mb-6 block"
            >
              Knowledge Hub
            </motion.span>
            
            <motion.h1 
              variants={itemVariants}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6"
            >
              Resources for Health Systems Transformation
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-slate-600 leading-relaxed"
            >
              Access our collection of research publications, policy briefs, case studies, and 
              expert insights to support evidence-based health system strengthening.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-40">
        <div className="container-main">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-xl">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-hsl pl-11 w-full"
                data-testid="resources-search"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`filter-${category.toLowerCase().replace(' ', '-')}`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="input-hsl w-auto"
              data-testid="type-filter"
            >
              {resourceTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="section-padding" data-testid="featured-resources">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Featured
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900">
              Essential Resources
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {featuredResources.map((resource) => (
              <ResourceCard 
                key={resource.id} 
                resource={resource} 
                onDownload={trackDownload}
              />
            ))}
          </div>
        </div>
      </section>

      {/* All Resources */}
      <section className="section-padding bg-slate-50" data-testid="all-resources">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-2">
              All Resources
            </h2>
            <p className="text-slate-500">
              {filteredResources.length} resources available
            </p>
          </motion.div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={40} className="animate-spin text-teal-600" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredResources.map((resource) => (
                <ResourceCard 
                  key={resource.id} 
                  resource={resource} 
                  onDownload={trackDownload}
                />
              ))}
            </div>
          )}

          {!isLoading && filteredResources.length === 0 && (
            <div className="bg-white border border-slate-200 p-12 text-center">
              <FileText size={48} className="text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-slate-900 mb-2">No resources found</h3>
              <p className="text-slate-500">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Resources Section */}
      <section className="section-padding" data-testid="video-resources">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Video Library
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Watch & Learn
            </h2>
            <p className="text-slate-600">
              Explore our video content featuring expert discussions, webinars, and training materials.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Introduction to the HSL Impact Model',
                duration: '15:32',
                thumbnail: 'https://images.unsplash.com/photo-1536064479547-7ee40b74b807?w=400&q=80',
              },
              {
                title: 'Analytical Methodology Explained',
                duration: '22:15',
                thumbnail: 'https://images.pexels.com/photos/6476782/pexels-photo-6476782.jpeg?auto=compress&cs=tinysrgb&w=400',
              },
              {
                title: 'Building Resilient Health Systems',
                duration: '45:00',
                thumbnail: 'https://images.unsplash.com/photo-1678695972687-033fa0bdbac9?w=400&q=80',
              },
            ].map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                data-testid={`video-card-${index}`}
              >
                <div className="relative aspect-video overflow-hidden mb-4">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                      <Play size={24} className="text-teal-600 ml-1" />
                    </div>
                  </div>
                  <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1">
                    {video.duration}
                  </span>
                </div>
                <h3 className="font-heading text-lg text-slate-900 group-hover:text-teal-600 transition-colors">
                  {video.title}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Resources */}
      <section className="section-padding bg-slate-50" data-testid="partner-resources">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4 block">
              Partner Network
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              External Resources
            </h2>
            <p className="text-slate-600">
              Access resources from our global health partners and collaborators.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerLinks.map((partner, index) => (
              <motion.a
                key={index}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 p-6 hover:border-teal-200 transition-colors group"
                data-testid={`partner-link-${index}`}
              >
                <div className="w-12 h-12 bg-teal-100 border border-teal-200 flex items-center justify-center mb-4">
                  <span className="font-mono text-xs font-bold text-teal-600">{partner.logo}</span>
                </div>
                <h3 className="font-heading text-lg text-slate-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {partner.name}
                </h3>
                <p className="text-slate-500 text-sm mb-4">{partner.description}</p>
                <span className="text-teal-600 text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                  Visit Site <ArrowRight size={14} />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding" data-testid="newsletter-cta">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden bg-slate-900 p-12 md:p-16 text-center"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/10 via-transparent to-teal-500/10" />
            
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
                Stay Updated
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
                Subscribe to receive the latest resources, research publications, and insights 
                on health systems transformation.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  className="btn-primary whitespace-nowrap"
                  data-testid="newsletter-submit"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
